import { useState } from 'react';
import { Link, useParams, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Check, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import type { PipelineScenario } from '@jeraisonne/db';
import { usePipelineScenarios } from '@/lib/admin/pipeline-queries';
import { useUpdateScenario, useStartMatching } from '@/lib/admin/pipeline-mutations';
import { SCENARIO_STATUS_LABEL, SCENARIO_STATUS_CLS } from '@/lib/admin/pipeline-labels';

export function TutorAdminPipelineScenariosPage() {
  const { runId } = useParams({ strict: false }) as { runId: string };
  const navigate = useNavigate();
  const { data: scenarios, isLoading } = usePipelineScenarios(runId, { live: true });
  const startMatching = useStartMatching();

  const blocking = (scenarios ?? []).filter(
    (s) => s.status === 'pending_review' || s.status === 'regenerating',
  );
  const kept = (scenarios ?? []).filter((s) => s.status !== 'discarded');
  const canLaunchMatching =
    (scenarios ?? []).length > 0 && blocking.length === 0 && !startMatching.isPending;

  async function handleStartMatching() {
    if (!canLaunchMatching) return;
    await startMatching.mutateAsync(runId);
    navigate({ to: '/tutor/admin/pipeline' });
  }

  return (
    <div className="mx-auto max-w-3xl p-4 pb-28 sm:p-6">
      <Link
        to="/tutor/admin/pipeline"
        className="text-ink-500 hover:text-ink-900 mb-4 inline-flex items-center gap-1.5 font-sans text-[13px] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Pipeline
      </Link>

      <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
        Valider les scénarios
      </h1>
      <p className="text-ink-500 mt-1 font-sans text-sm">
        Étape 4 — valide, modifie ou régénère chaque scénario avant le matching.
      </p>

      {isLoading ? (
        <div className="mt-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {kept.map((scenario) => (
            <ScenarioCard key={scenario.id} runId={runId} scenario={scenario} />
          ))}
        </div>
      )}

      {startMatching.isError && (
        <p className="text-accent-600 mt-4 font-sans text-[13px]">{startMatching.error?.message}</p>
      )}

      {/* Barre d'action sticky */}
      <div className="border-ink-200 fixed inset-x-0 bottom-0 border-t bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:left-60">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          <span className="text-ink-500 font-sans text-xs">
            {blocking.length > 0
              ? `${blocking.length} scénario(s) à traiter`
              : 'Tous les scénarios sont traités'}
          </span>
          <Button onClick={handleStartMatching} disabled={!canLaunchMatching} className="min-h-11">
            {startMatching.isPending ? 'Lancement…' : 'Lancer le matching'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ScenarioCard({ runId, scenario }: { runId: string; scenario: PipelineScenario }) {
  const update = useUpdateScenario();
  const [mode, setMode] = useState<'view' | 'edit' | 'regenerate'>('view');
  const [title, setTitle] = useState(scenario.title);
  const [pitch, setPitch] = useState(scenario.narrative_pitch);
  const [question, setQuestion] = useState(scenario.central_question);
  const [instructions, setInstructions] = useState('');

  const pending = scenario.status === 'pending_review';
  const editable = pending && update.isIdle;

  async function act(action: 'validate' | 'discard') {
    await update.mutateAsync({ runId, scenarioId: scenario.id, action });
  }

  async function saveEdit() {
    await update.mutateAsync({
      runId,
      scenarioId: scenario.id,
      action: 'edit',
      edits: {
        title: title.trim(),
        narrative_pitch: pitch.trim(),
        central_question: question.trim(),
      },
    });
    setMode('view');
  }

  async function submitRegenerate() {
    if (!instructions.trim()) return;
    await update.mutateAsync({
      runId,
      scenarioId: scenario.id,
      action: 'regenerate',
      instructions: instructions.trim(),
    });
    setMode('view');
    setInstructions('');
  }

  return (
    <div className="border-ink-200 rounded-2xl border bg-white p-4 sm:p-5">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'font-display inline-flex h-6 items-center rounded-full border px-2.5 text-[11px] font-bold',
            SCENARIO_STATUS_CLS[scenario.status],
          )}
        >
          {SCENARIO_STATUS_LABEL[scenario.status]}
        </span>
        {scenario.interest_score != null && (
          <span className="text-ink-500 font-mono text-[11px]">
            intérêt {scenario.interest_score} · pertinence {scenario.relevance_score} · originalité{' '}
            {scenario.originality_score}
          </span>
        )}
      </div>

      {mode === 'edit' ? (
        <div className="space-y-2">
          <Field label="Titre" value={title} onChange={setTitle} />
          <Field label="Pitch narratif" value={pitch} onChange={setPitch} multiline />
          <Field label="Question centrale" value={question} onChange={setQuestion} multiline />
          <div className="flex gap-2 pt-1">
            <Button onClick={saveEdit} disabled={update.isPending} className="min-h-11">
              {update.isPending ? 'Enregistrement…' : 'Enregistrer & valider'}
            </Button>
            <Button variant="outline" onClick={() => setMode('view')} className="min-h-11">
              Annuler
            </Button>
          </div>
        </div>
      ) : (
        <>
          <h3 className="font-display text-ink-900 text-[15px] font-extrabold">{scenario.title}</h3>
          <p className="text-ink-700 mt-1 font-sans text-[13px] leading-relaxed">
            {scenario.narrative_pitch}
          </p>
          <p className="text-ink-600 mt-2 font-sans text-[13px]">
            <span className="font-semibold">Question : </span>
            {scenario.central_question}
          </p>
          {scenario.audit_reasoning && (
            <p className="text-ink-400 mt-2 font-sans text-[12px] italic">
              {scenario.audit_reasoning}
            </p>
          )}
        </>
      )}

      {mode === 'regenerate' && (
        <div className="mt-3 space-y-2">
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={3}
            placeholder="Instructions pour la régénération…"
            className="border-ink-200 focus:ring-primary-400 w-full resize-none rounded-xl border px-3 py-2.5 font-sans text-[13px] focus:outline-none focus:ring-2"
          />
          <div className="flex gap-2">
            <Button
              onClick={submitRegenerate}
              disabled={update.isPending || !instructions.trim()}
              className="min-h-11"
            >
              {update.isPending ? 'Envoi…' : 'Régénérer'}
            </Button>
            <Button variant="outline" onClick={() => setMode('view')} className="min-h-11">
              Annuler
            </Button>
          </div>
        </div>
      )}

      {pending && mode === 'view' && (
        <div className="mt-3 flex flex-wrap gap-2">
          <ActionBtn icon={Check} label="Valider" onClick={() => act('validate')} primary />
          <ActionBtn
            icon={Pencil}
            label="Modifier"
            onClick={() => {
              setTitle(scenario.title);
              setPitch(scenario.narrative_pitch);
              setQuestion(scenario.central_question);
              setMode('edit');
            }}
          />
          <ActionBtn icon={RefreshCw} label="Régénérer" onClick={() => setMode('regenerate')} />
          <ActionBtn icon={Trash2} label="Supprimer" onClick={() => act('discard')} danger />
        </div>
      )}

      {!editable && pending && mode === 'view' && update.isPending && (
        <p className="text-ink-400 mt-2 font-sans text-[12px]">En cours…</p>
      )}
      {update.isError && (
        <p className="text-accent-600 mt-2 font-sans text-[12px]">{update.error?.message}</p>
      )}
    </div>
  );
}

function ActionBtn({
  icon: Icon,
  label,
  onClick,
  primary,
  danger,
}: {
  icon: typeof Check;
  label: string;
  onClick: () => void;
  primary?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'font-display inline-flex min-h-11 items-center gap-1.5 rounded-xl border px-3.5 text-[13px] font-bold transition-all active:scale-[.98]',
        primary && 'bg-primary-600 border-primary-600 hover:bg-primary-700 text-white',
        danger && 'border-accent-200 text-accent-700 hover:bg-accent-50',
        !primary && !danger && 'border-ink-200 text-ink-700 hover:bg-ink-50 bg-white',
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function Field({
  label,
  value,
  onChange,
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
}) {
  const cls =
    'border-ink-200 focus:ring-primary-400 w-full rounded-xl border px-3 py-2.5 font-sans text-[13px] focus:outline-none focus:ring-2';
  return (
    <label className="flex flex-col gap-1">
      <span className="font-display text-ink-700 text-[12px] font-semibold">{label}</span>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className={cn(cls, 'resize-none')}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cls}
        />
      )}
    </label>
  );
}
